import { Crm } from '../../../domain/crm';
import type { OpportunityList } from '../../../domain/list';
import { useCrmStatusQuery } from '../../../features/crm/useCrmStatusQuery';
import { useQuota } from '../../../features/quota/hooks/useQuota';
import { useUserQuery } from '../../../features/user/useUserQuery';
import { useDomainsQuota } from '../../../hooks/useDomainsQuota';
import { useDispatchTrackEventWithLocation } from '../../../services/tracking/hooks/useDispatchTrackEventWithLocation';

const getEventSubName = (isHsEnabled: boolean, isSfEnabled: boolean) => {
  if (isHsEnabled && isSfEnabled) {
    return `${Crm.salesforce}, ${Crm.hubspot}`;
  }
  return isHsEnabled ? Crm.hubspot : Crm.salesforce;
};

export const useCompanyActionTracking = () => {
  const { quota } = useQuota();
  const { data: hsStatus } = useCrmStatusQuery(Crm.hubspot);
  const { data: sfStatus } = useCrmStatusQuery(Crm.salesforce);
  const { data: user } = useUserQuery();
  const uniqueDomainsCount = useDomainsQuota();
  const remainingDomainsCount = user?.opportunitiesLimit ? user.opportunitiesLimit - uniqueDomainsCount : 0;

  const dispatchTrackEventWithLocation = useDispatchTrackEventWithLocation();

  const getStatus = () => {
    const isHsAccountExportEnabled = !!hsStatus?.isAccountExportEnabled;
    const isSfAccountExportEnabled = !!sfStatus?.isAccountExportEnabled;

    if (isHsAccountExportEnabled || isSfAccountExportEnabled) {
      return `account export active/${getEventSubName(isHsAccountExportEnabled, isSfAccountExportEnabled)}`;
    }

    if (!!hsStatus || !!sfStatus) {
      return `account export inactive/${getEventSubName(!!hsStatus, !!sfStatus)}`;
    }

    return 'crm not integrated';
  };

  const handleTrackAction = (action: string, eventName: string) => {
    dispatchTrackEventWithLocation('company export', action, eventName);
  };

  const handleTrackOpenDropdown = () => {
    handleTrackAction('open export to options', getStatus());
  };

  const handleTrackExportAction = (action: string, crm: Crm) => {
    handleTrackAction(action, `export to ${crm}/${quota.remaining}`);
  };

  const handleTrackListAction = (action: string, eventName: string) => {
    handleTrackAction(action, `${eventName}/${uniqueDomainsCount}/${remainingDomainsCount}`);
  };

  const handleTrackOnSaveToList = ({ name, id }: OpportunityList) => {
    handleTrackAction('save to list', `${id ? 'existing list' : 'new list'}/${name}/${remainingDomainsCount}`);
  };

  return {
    handleTrackOpenDropdown,
    handleTrackListAction,
    handleTrackOnSaveToList,
    handleTrackExportAction,
  };
};
